import clsx from 'clsx'
import React from 'react'

import { Theme, useTheme } from '../Theme'
import { warn } from '../utils/console'
import { PAYMENT_PROGRESS_CIRCLE_DISPLAY_NAME, SIZES } from './constants'
import { PaymentProgressProps, PaymentProgressSizes } from './PaymentProgress'
import styles from './style.module.scss'
import { calcPercent } from './utils'

const WIDTH: Record<string, number> = {
  [SIZES.EXTRA_LARGE]: 128,
  [SIZES.LARGE]: 64,
  [SIZES.REGULAR]: 48,
  [SIZES.SMALL]: 40,
  [SIZES.EXTRA_SMALL]: 32,
}

const INNER_WIDTH: Record<string, number> = {
  [SIZES.EXTRA_LARGE]: 120,
  [SIZES.LARGE]: 58,
  [SIZES.REGULAR]: 42,
  [SIZES.SMALL]: 34,
  [SIZES.EXTRA_SMALL]: 26,
}

export const STROKE_WIDTH: Record<PaymentProgressSizes, Record<Theme, number>> = {
  [SIZES.EXTRA_LARGE]: {
    [Theme.AP]: 14,
    [Theme.Pegasus]: 14,
    [Theme.Kelpie]: 14,
    [Theme.KelpieMint]: 14,
    [Theme.Cash]: 17,
  },
  [SIZES.LARGE]: {
    [Theme.AP]: 7,
    [Theme.Pegasus]: 7,
    [Theme.Kelpie]: 7,
    [Theme.KelpieMint]: 7,
    [Theme.Cash]: 9,
  },
  [SIZES.REGULAR]: {
    [Theme.AP]: 4,
    [Theme.Pegasus]: 4,
    [Theme.Kelpie]: 4,
    [Theme.KelpieMint]: 4,
    [Theme.Cash]: 7,
  },
  [SIZES.SMALL]: {
    [Theme.AP]: 4,
    [Theme.Pegasus]: 4,
    [Theme.Kelpie]: 4,
    [Theme.KelpieMint]: 4,
    [Theme.Cash]: 6,
  },
  [SIZES.EXTRA_SMALL]: {
    [Theme.AP]: 4,
    [Theme.Pegasus]: 4,
    [Theme.Kelpie]: 4,
    [Theme.KelpieMint]: 4,
    [Theme.Cash]: 6,
  },
} as const

export const Circle = React.forwardRef<HTMLDivElement, PaymentProgressProps>(
  ({ size = SIZES.LARGE, step = 2, maxStep = 4, paymentLabel, paymentDescription, title, description, isSubsidiary, ...otherProps }, ref): JSX.Element => {
    const percentage = calcPercent(step, maxStep)
    const width = WIDTH[size]
    const innerWidth = INNER_WIDTH[size]
    const strokeWidth = STROKE_WIDTH[size]
    const theme = useTheme()
    const classes = clsx(isSubsidiary ? styles.progressCircleSubsidiary : styles.progressCircle)

    const hasAccessibilityProp = typeof title !== 'undefined'
    if (!hasAccessibilityProp) {
      warn('[Core - PaymentProgress] Component is used without accessibility attribute. Please include "title" prop to meet AA compliance.')
    }

    const relativeStrokeWidth = (): string => {
      const selectedStrokeWidth = strokeWidth[theme.theme.selected]

      return ((selectedStrokeWidth / width) * 100).toFixed(1)
    }

    const trackPath = (): string => {
      const radius = 50 - parseFloat(relativeStrokeWidth()) / 2
      return `M 50 50 m 0 -${radius} a ${radius} ${radius} 0 1 1 0 ${radius * 2} a ${radius} ${radius} 0 1 1 0 -${radius * 2}`
    }

    const perimeter = (): number => {
      const radius = 50 - parseFloat(relativeStrokeWidth()) / 2
      return 2 * Math.PI * radius
    }

    const circlePathStyle = (): React.CSSProperties => {
      const _perimeter = perimeter()
      const strokeDash = 1 - percentage / 100
      return {
        strokeDasharray: `${_perimeter}px, ${_perimeter}px`,
        strokeDashoffset: `${strokeDash * _perimeter}px`,
      }
    }

    const innerLabelClassNames = clsx(styles.innerLabel, styles[`innerLabelSize${size}`])
    const renderLabel = (): React.ReactNode => {
      if (size === SIZES.EXTRA_LARGE) {
        return (
          <>
            <div className={innerLabelClassNames}>{paymentLabel ?? step}</div>
            {typeof paymentDescription !== 'undefined' && <div className={styles.innerDescription}>{paymentDescription}</div>}
          </>
        )
      }
      return <div className={innerLabelClassNames}>{paymentLabel ?? step}</div>
    }

    return (
      <div
        {...otherProps}
        ref={ref}
        className={classes}
        style={{ width, height: width }}
        role='progressbar'
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={percentage}
      >
        <svg viewBox='0 0 100 100' width={innerWidth} height={innerWidth}>
          {typeof title !== 'undefined' && <title>{title}</title>}
          {typeof description !== 'undefined' && <desc>{description}</desc>}
          <path className={styles.strokeBg} d={trackPath()} strokeWidth={relativeStrokeWidth()} />
          <path className={styles.strokePath} d={trackPath()} strokeLinecap='round' strokeWidth={relativeStrokeWidth()} style={circlePathStyle()} />
        </svg>
        <div className={styles.innerLabelContainer}>{renderLabel()}</div>
      </div>
    )
  }
)

Circle.displayName = PAYMENT_PROGRESS_CIRCLE_DISPLAY_NAME
